import React from 'react';
import { clsx } from 'clsx';
// H-1: replaces the local sanitizeRichTextHtml, which was never exported -
// the direct cause of the hand-rolled clones in apps/web and apps/customer-portal.
import { sanitizeHtml } from '../security/sanitize-html';

/**
 * Strip anything executable from author-supplied HTML before it is injected into
 * the DOM via `innerHTML` / `dangerouslySetInnerHTML`. Knowledge-base article
 * content flows through these editors/displays, so unsanitised HTML is a stored
 * XSS vector (bug #9158 / #9623): a stored `<img onerror=...>` or `<script>` runs
 * on view. Removes script/style/embeds, all inline event handlers (on*) and
 * javascript: URLs while preserving normal formatting. `DOMParser` builds an
 * INERT document (no script execution, no image loads), so parsing the payload
 * here does not fire the handler; only the cleaned markup is returned. SSR / no
 * DOMParser falls back to returning the input unchanged (never runs there).
 */

// Toolbar button type
export type ToolbarButton =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'bulletList'
  | 'orderedList'
  | 'blockquote'
  | 'code'
  | 'link'
  | 'image'
  | 'alignLeft'
  | 'alignCenter'
  | 'alignRight'
  | 'undo'
  | 'redo'
  | 'clear';

// Toolbar button config
interface ToolbarButtonConfig {
  icon: React.ReactNode;
  title: string;
  command: string;
  value?: string;
}

// Toolbar icons
const toolbarIcons: Record<ToolbarButton, ToolbarButtonConfig> = {
  bold: {
    icon: <span className="font-bold">B</span>,
    title: 'Bold',
    command: 'bold',
  },
  italic: {
    icon: <span className="italic">I</span>,
    title: 'Italic',
    command: 'italic',
  },
  underline: {
    icon: <span className="underline">U</span>,
    title: 'Underline',
    command: 'underline',
  },
  strikethrough: {
    icon: <span className="line-through">S</span>,
    title: 'Strikethrough',
    command: 'strikeThrough',
  },
  heading1: {
    icon: <span className="font-bold text-sm">H1</span>,
    title: 'Heading 1',
    command: 'formatBlock',
    value: 'h1',
  },
  heading2: {
    icon: <span className="font-bold text-sm">H2</span>,
    title: 'Heading 2',
    command: 'formatBlock',
    value: 'h2',
  },
  heading3: {
    icon: <span className="font-bold text-sm">H3</span>,
    title: 'Heading 3',
    command: 'formatBlock',
    value: 'h3',
  },
  bulletList: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    ),
    title: 'Bullet List',
    command: 'insertUnorderedList',
  },
  orderedList: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
      </svg>
    ),
    title: 'Numbered List',
    command: 'insertOrderedList',
  },
  blockquote: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
    title: 'Blockquote',
    command: 'formatBlock',
    value: 'blockquote',
  },
  code: {
    icon: <span className="font-mono text-sm">&lt;/&gt;</span>,
    title: 'Code',
    command: 'formatBlock',
    value: 'pre',
  },
  link: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    ),
    title: 'Insert Link',
    command: 'createLink',
  },
  image: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Insert Image',
    command: 'insertImage',
  },
  alignLeft: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h16" />
      </svg>
    ),
    title: 'Align Left',
    command: 'justifyLeft',
  },
  alignCenter: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M4 18h16" />
      </svg>
    ),
    title: 'Align Center',
    command: 'justifyCenter',
  },
  alignRight: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M4 18h16" />
      </svg>
    ),
    title: 'Align Right',
    command: 'justifyRight',
  },
  undo: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
      </svg>
    ),
    title: 'Undo',
    command: 'undo',
  },
  redo: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" />
      </svg>
    ),
    title: 'Redo',
    command: 'redo',
  },
  clear: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    ),
    title: 'Clear Formatting',
    command: 'removeFormat',
  },
};

// Default toolbar configuration
export const defaultToolbarButtons: ToolbarButton[][] = [
  ['bold', 'italic', 'underline', 'strikethrough'],
  ['heading1', 'heading2', 'heading3'],
  ['bulletList', 'orderedList', 'blockquote'],
  ['alignLeft', 'alignCenter', 'alignRight'],
  ['link', 'image', 'code'],
  ['undo', 'redo', 'clear'],
];

// Toolbar button component
export interface EditorToolbarButtonProps {
  button: ToolbarButton;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  className?: string;
}

export function EditorToolbarButton({
  button,
  onClick,
  active = false,
  disabled = false,
  className,
}: EditorToolbarButtonProps) {
  const config = toolbarIcons[button];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={config.title}
      className={clsx(
        'p-1.5 rounded transition-colors',
        active
          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {config.icon}
    </button>
  );
}

// Editor toolbar component
export interface EditorToolbarProps {
  buttons?: ToolbarButton[][];
  onCommand: (command: string, value?: string) => void;
  activeFormats?: Set<string>;
  disabled?: boolean;
  className?: string;
}

export function EditorToolbar({
  buttons = defaultToolbarButtons,
  onCommand,
  activeFormats = new Set(),
  disabled = false,
  className,
}: EditorToolbarProps) {
  const handleClick = (button: ToolbarButton) => {
    const config = toolbarIcons[button];
    onCommand(config.command, config.value);
  };

  const isActive = (button: ToolbarButton) => {
    const config = toolbarIcons[button];
    return activeFormats.has(config.command) || activeFormats.has(button);
  };

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 dark:bg-gray-900 dark:border-gray-700',
        className
      )}
      role="toolbar"
      aria-label="Text formatting"
    >
      {buttons.map((group, groupIndex) => (
        <React.Fragment key={groupIndex}>
          {groupIndex > 0 && (
            <div className="w-px h-6 bg-gray-300 mx-1" aria-hidden="true" />
          )}
          <div className="flex items-center gap-0.5">
            {group.map((button) => (
              <EditorToolbarButton
                key={button}
                button={button}
                onClick={() => handleClick(button)}
                active={isActive(button)}
                disabled={disabled}
              />
            ))}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// Rich text editor props
export interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  toolbarButtons?: ToolbarButton[][];
  showToolbar?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  minHeight?: number;
  maxHeight?: number;
  className?: string;
}

export function RichTextEditor({
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder = 'Start typing...',
  toolbarButtons = defaultToolbarButtons,
  showToolbar = true,
  disabled = false,
  readOnly = false,
  minHeight = 150,
  maxHeight,
  className,
}: RichTextEditorProps) {
  const editorRef = React.useRef<HTMLDivElement>(null);
  const [activeFormats, setActiveFormats] = React.useState<Set<string>>(new Set());
  const [isFocused, setIsFocused] = React.useState(false);

  // Set initial content (sanitised — the value may be stored, author-supplied
  // article HTML; #9158). Compare against the sanitised form so we don't loop.
  React.useEffect(() => {
    const clean = sanitizeHtml(value);
    if (editorRef.current && clean !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = clean;
    }
  }, [value]);

  // Update active formats on selection change
  const updateActiveFormats = React.useCallback(() => {
    const formats = new Set<string>();

    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('underline')) formats.add('underline');
    if (document.queryCommandState('strikeThrough')) formats.add('strikethrough');
    if (document.queryCommandState('insertUnorderedList')) formats.add('bulletList');
    if (document.queryCommandState('insertOrderedList')) formats.add('orderedList');
    if (document.queryCommandState('justifyLeft')) formats.add('alignLeft');
    if (document.queryCommandState('justifyCenter')) formats.add('alignCenter');
    if (document.queryCommandState('justifyRight')) formats.add('alignRight');

    // Block-level formats — queryCommandState doesn't return useful values
    // for formatBlock, so we read the actual block tag and map it.
    const block = (document.queryCommandValue('formatBlock') || '').toLowerCase();
    if (block === 'h1') formats.add('heading1');
    else if (block === 'h2') formats.add('heading2');
    else if (block === 'h3') formats.add('heading3');
    else if (block === 'blockquote') formats.add('blockquote');
    else if (block === 'pre') formats.add('code');

    setActiveFormats(formats);
  }, []);

  // Handle selection change
  React.useEffect(() => {
    const handleSelectionChange = () => {
      if (isFocused) {
        updateActiveFormats();
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [isFocused, updateActiveFormats]);

  // Execute command
  const executeCommand = (command: string, value?: string) => {
    if (disabled || readOnly) return;

    editorRef.current?.focus();

    if (command === 'createLink') {
      // execCommand('createLink') only works when text is selected. If the
      // user clicked the link button without selecting anything, prompt for
      // both the URL and the visible label and insert a fresh anchor.
      //
      // IMPORTANT: Save the selection BEFORE showing the prompt, because
      // prompt() is a blocking call that causes the browser to lose focus
      // from the contenteditable area, which clears the selection.
      const sel = window.getSelection();
      const hasSelection = !!sel && sel.toString().length > 0;
      const selectedText = sel?.toString() || '';

      // Save selection range before prompt steals focus
      let savedRange: Range | null = null;
      if (sel && sel.rangeCount > 0) {
        savedRange = sel.getRangeAt(0).cloneRange();
      }

      const url = prompt('Enter URL:');
      if (!url) return;
      const safeUrl = /^(https?:|mailto:|tel:|\/|#)/i.test(url) ? url : `https://${url}`;

      // Restore focus and selection after prompt
      editorRef.current?.focus();
      if (savedRange) {
        const newSel = window.getSelection();
        newSel?.removeAllRanges();
        newSel?.addRange(savedRange);
      }

      if (hasSelection) {
        document.execCommand('createLink', false, safeUrl);
        // Add target="_blank" to the created link
        const newSel = window.getSelection();
        if (newSel && newSel.anchorNode) {
          const anchor = newSel.anchorNode.parentElement?.closest('a') ||
                         newSel.anchorNode.parentElement?.querySelector('a') ||
                         (newSel.anchorNode as HTMLElement).querySelector?.('a');
          if (anchor) {
            anchor.setAttribute('target', '_blank');
            anchor.setAttribute('rel', 'noopener noreferrer');
          }
        }
      } else {
        const label = prompt('Link text:', safeUrl) || safeUrl;
        // After second prompt, restore focus and selection again
        editorRef.current?.focus();
        if (savedRange) {
          const newSel = window.getSelection();
          newSel?.removeAllRanges();
          newSel?.addRange(savedRange);
        }
        document.execCommand(
          'insertHTML',
          false,
          `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`,
        );
      }
    } else if (command === 'insertImage') {
      // Save selection range before prompt steals focus
      const sel = window.getSelection();
      let savedRange: Range | null = null;
      if (sel && sel.rangeCount > 0) {
        savedRange = sel.getRangeAt(0).cloneRange();
      }

      const url = prompt('Enter image URL:');
      if (!url) return;
      // Only allow http(s) and data URLs to avoid javascript: and other
      // protocols that would XSS through innerHTML.
      if (!/^(https?:|data:image\/)/i.test(url)) {
        alert('Please enter a valid image URL (http://, https://, or data:image/).');
        return;
      }

      // Restore focus and selection after prompt
      editorRef.current?.focus();
      if (savedRange) {
        const newSel = window.getSelection();
        newSel?.removeAllRanges();
        newSel?.addRange(savedRange);
      }

      document.execCommand('insertImage', false, url);
    } else if (command === 'formatBlock' && value) {
      // Toggle: if the current block is already the requested tag, switch
      // back to a plain paragraph instead of leaving the user trapped in
      // H1/blockquote/code with no obvious way out.
      const current = (document.queryCommandValue('formatBlock') || '').toLowerCase();
      const target = current === value.toLowerCase() ? 'p' : value;
      document.execCommand('formatBlock', false, `<${target}>`);
    } else if (command === 'removeFormat') {
      // Native removeFormat only strips inline marks (bold/italic/etc.).
      // Reset the block tag too so headings/blockquote/code revert to a
      // plain paragraph — what users expect from a "clear" button.
      document.execCommand('removeFormat', false);
      document.execCommand('formatBlock', false, '<p>');
    } else {
      document.execCommand(command, false, value);
    }

    handleInput();
    updateActiveFormats();
  };

  // Handle input changes
  const handleInput = () => {
    if (editorRef.current) {
      onChange?.(editorRef.current.innerHTML);
    }
  };

  // Handle focus
  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  // Handle blur
  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled || readOnly) return;

    if (e.metaKey || e.ctrlKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          executeCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          executeCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          executeCommand('underline');
          break;
        case 'z':
          if (e.shiftKey) {
            e.preventDefault();
            executeCommand('redo');
          } else {
            e.preventDefault();
            executeCommand('undo');
          }
          break;
      }
    }
  };

  const isEmpty = !value || value === '<br>' || value === '<p><br></p>';

  return (
    <div
      className={clsx(
        'border border-gray-300 rounded-lg overflow-hidden dark:border-gray-700',
        isFocused && 'ring-2 ring-blue-500 border-blue-500',
        disabled && 'bg-gray-100 opacity-75 dark:bg-gray-800',
        className
      )}
    >
      {showToolbar && (
        <EditorToolbar
          buttons={toolbarButtons}
          onCommand={executeCommand}
          activeFormats={activeFormats}
          disabled={disabled || readOnly}
        />
      )}

      <div className="relative">
        <div
          ref={editorRef}
          contentEditable={!disabled && !readOnly}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={clsx(
            'prose prose-sm max-w-none p-3 focus:outline-none overflow-auto',
            disabled && 'cursor-not-allowed',
            readOnly && 'cursor-default'
          )}
          style={{
            minHeight: `${minHeight}px`,
            maxHeight: maxHeight ? `${maxHeight}px` : undefined,
          }}
          role="textbox"
          aria-multiline="true"
          aria-placeholder={placeholder}
          data-placeholder={placeholder}
        />

        {isEmpty && !isFocused && (
          <div
            className="absolute top-3 left-3 text-gray-400 pointer-events-none dark:text-gray-500"
            aria-hidden="true"
          >
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

// Simple rich text display (read-only)
export interface RichTextDisplayProps {
  html: string;
  className?: string;
}

export function RichTextDisplay({ html, className }: RichTextDisplayProps) {
  return (
    <div
      className={clsx('prose prose-sm max-w-none', className)}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  );
}

// Character counter
export interface CharacterCounterProps {
  current: number;
  max?: number;
  showRemaining?: boolean;
  className?: string;
}

export function CharacterCounter({
  current,
  max,
  showRemaining = true,
  className,
}: CharacterCounterProps) {
  const isOverLimit = max !== undefined && current > max;
  const remaining = max !== undefined ? max - current : undefined;

  return (
    <div
      className={clsx(
        'text-xs',
        isOverLimit ? 'text-red-500' : 'text-gray-500 dark:text-gray-400',
        className
      )}
    >
      {showRemaining && remaining !== undefined ? (
        <span>{remaining} characters remaining</span>
      ) : (
        <span>
          {current}
          {max !== undefined && ` / ${max}`}
        </span>
      )}
    </div>
  );
}

// Rich text editor with character limit
export interface RichTextEditorWithLimitProps extends RichTextEditorProps {
  maxLength?: number;
  showCharacterCount?: boolean;
}

export function RichTextEditorWithLimit({
  maxLength,
  showCharacterCount = true,
  onChange,
  ...props
}: RichTextEditorWithLimitProps) {
  const [charCount, setCharCount] = React.useState(0);

  const getTextContent = (html: string): string => {
    // Parse in an INERT DOMParser document instead of a live <div> so a stored
    // <img onerror=...> can't fire while we count characters (#9158).
    if (typeof DOMParser !== 'undefined') {
      return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
    }
    return html.replace(/<[^>]*>/g, '');
  };

  const handleChange = (html: string) => {
    const text = getTextContent(html);
    setCharCount(text.length);
    onChange?.(html);
  };

  return (
    <div>
      <RichTextEditor {...props} onChange={handleChange} />
      {showCharacterCount && (
        <div className="flex justify-end mt-1">
          <CharacterCounter current={charCount} max={maxLength} />
        </div>
      )}
    </div>
  );
}

// Minimal editor (no toolbar, just formatting shortcuts)
export interface MinimalEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function MinimalEditor({
  value = '',
  onChange,
  placeholder = 'Type here...',
  disabled = false,
  className,
}: MinimalEditorProps) {
  return (
    <RichTextEditor
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      showToolbar={false}
      minHeight={80}
      className={className}
    />
  );
}

// Inline editor (single line, basic formatting)
export interface InlineEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function InlineEditor({
  value = '',
  onChange,
  placeholder = 'Click to edit...',
  disabled = false,
  className,
}: InlineEditorProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const editorRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const clean = sanitizeHtml(value);
    if (editorRef.current && clean !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = clean;
    }
  }, [value]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
      setTimeout(() => editorRef.current?.focus(), 0);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (editorRef.current) {
      onChange?.(editorRef.current.innerHTML);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      editorRef.current?.blur();
    }
    if (e.key === 'Escape') {
      editorRef.current?.blur();
    }
  };

  const isEmpty = !value || value === '<br>';

  return (
    <div
      onClick={handleClick}
      className={clsx(
        'inline-block cursor-text',
        disabled && 'cursor-default opacity-75',
        className
      )}
    >
      <div
        ref={editorRef}
        contentEditable={isEditing && !disabled}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={clsx(
          'outline-none',
          isEditing && 'bg-yellow-50 px-1 -mx-1'
        )}
        suppressContentEditableWarning
      />
      {isEmpty && !isEditing && (
        <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
      )}
    </div>
  );
}
